import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, User, Mail, KeyRound, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

function Card({ icon: Icon, title, desc, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E5DEC9] bg-white p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-[#F0EAE1] text-[#8C532B] flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-[#1B3B2B]">{title}</h3>
          {desc && <p className="text-xs text-[#5F6B64] mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Account() {
  const { user, refreshUser } = useAuth();
  const outlet = useOutletContext();
  useEffect(() => { outlet?.setPageTitle?.("Account"); }, [outlet]);

  // Personal data
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Password change
  const [curPwd, setCurPwd] = useState("");
  const [nextPwd, setNextPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // Account deletion request (danger zone)
  const [delAcct, setDelAcct] = useState(false);
  const [sendingDeletion, setSendingDeletion] = useState(false);

  const requestAccountDeletion = async () => {
    setSendingDeletion(true);
    try {
      await api.post("/account/deletion-request");
      toast.success("Richiesta di eliminazione account inviata. Sarà gestita dal Platform Admin.");
      setDelAcct(false);
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSendingDeletion(false); }
  };

  useEffect(() => {
    setFirst(user?.first_name || "");
    setLast(user?.last_name || "");
    setPhone(user?.telefono || "");
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { first_name: first, last_name: last, telefono: phone });
      await refreshUser();
      toast.success("Dati personali aggiornati");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    try {
      await api.post("/auth/change-email/request", { new_email: newEmail.trim(), password: emailPwd });
      toast.success("Ti abbiamo inviato un'email di verifica al nuovo indirizzo. Conferma dal link per completare il cambio.");
      setNewEmail(""); setEmailPwd("");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSendingEmail(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (nextPwd !== confirmPwd) { toast.error("Le nuove password non coincidono"); return; }
    if (nextPwd.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: nextPwd });
      toast.success("Password aggiornata");
      setCurPwd(""); setNextPwd(""); setConfirmPwd("");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSavingPwd(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="account-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#1B3B2B]">Account</h1>
        <p className="text-sm text-[#5F6B64] mt-1">Gestisci i tuoi dati di accesso e le informazioni personali.</p>
      </div>

      <Card icon={User} title="Dati personali" testid="account-personal-card">
        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first">Nome</Label>
              <Input id="first" data-testid="account-first-name" value={first} onChange={(e) => setFirst(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="last">Cognome</Label>
              <Input id="last" data-testid="account-last-name" value={last} onChange={(e) => setLast(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label htmlFor="phone">Telefono</Label>
            <Input id="phone" data-testid="account-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="+39 …" />
          </div>
          <Button type="submit" disabled={savingProfile} data-testid="account-save-profile"
            className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {savingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva dati personali
          </Button>
        </form>
      </Card>

      <Card icon={Mail} title="Email di accesso"
        desc="È l'indirizzo con cui accedi. È distinto dagli indirizzi email di contatto della scuderia."
        testid="account-email-card">
        <div className="rounded-lg bg-[#F0EAE1] px-3 py-2 text-sm text-[#1B3B2B] mb-4" data-testid="account-current-email">
          Email attuale: <strong>{user?.email}</strong>
        </div>
        <form onSubmit={requestEmailChange} className="space-y-4">
          <div>
            <Label htmlFor="newEmail">Nuovo indirizzo email</Label>
            <Input id="newEmail" type="email" required data-testid="account-new-email" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} className="mt-1.5" placeholder="nuova@esempio.it" />
          </div>
          <div>
            <Label htmlFor="emailPwd">Conferma con la password attuale</Label>
            <Input id="emailPwd" type="password" required data-testid="account-email-password" value={emailPwd}
              onChange={(e) => setEmailPwd(e.target.value)} className="mt-1.5" placeholder="••••••••" />
          </div>
          <p className="text-xs text-[#5F6B64]">Invieremo un link di verifica al nuovo indirizzo. Il cambio diventa effettivo solo dopo la conferma. Non è richiesta l'approvazione del Platform Admin.</p>
          <Button type="submit" disabled={sendingEmail} data-testid="account-request-email-change"
            className="bg-[#8C532B] hover:bg-[#743f1f]">
            {sendingEmail && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Invia link di verifica
          </Button>
        </form>
      </Card>

      <Card icon={KeyRound} title="Password" testid="account-password-card">
        <form onSubmit={changePassword} className="space-y-4">
          <div>
            <Label htmlFor="curPwd">Password attuale</Label>
            <Input id="curPwd" type="password" required data-testid="account-current-password" value={curPwd}
              onChange={(e) => setCurPwd(e.target.value)} className="mt-1.5" placeholder="••••••••" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nextPwd">Nuova password</Label>
              <Input id="nextPwd" type="password" required data-testid="account-new-password" value={nextPwd}
                onChange={(e) => setNextPwd(e.target.value)} className="mt-1.5" placeholder="min. 8 caratteri" />
            </div>
            <div>
              <Label htmlFor="confirmPwd">Conferma nuova password</Label>
              <Input id="confirmPwd" type="password" required data-testid="account-confirm-password" value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)} className="mt-1.5" placeholder="••••••••" />
            </div>
          </div>
          <Button type="submit" disabled={savingPwd} data-testid="account-change-password"
            className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {savingPwd && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Aggiorna password
          </Button>
        </form>
      </Card>

      <div data-testid="account-danger-card" className="rounded-xl border border-red-200 bg-red-50/60 p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-red-700">Zona pericolosa</h3>
            <p className="text-xs text-[#5F6B64] mt-0.5">
              La richiesta di eliminazione viene gestita dal Platform Admin. Il tuo account e le tue membership non vengono eliminati subito.
            </p>
          </div>
        </div>
        <Button variant="outline" data-testid="request-account-deletion" onClick={() => setDelAcct(true)}
          className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800">
          Richiedi eliminazione account
        </Button>
      </div>

      <Dialog open={delAcct} onOpenChange={setDelAcct}>
        <DialogContent data-testid="account-deletion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Richiedere l'eliminazione dell'account?</DialogTitle>
            <DialogDescription>
              Verrà creata una richiesta pendente gestita dal Platform Admin. Il tuo account e le tue membership NON vengono eliminati ora.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAcct(false)} data-testid="account-deletion-cancel">Annulla</Button>
            <Button onClick={requestAccountDeletion} disabled={sendingDeletion} data-testid="account-deletion-confirm"
              className="bg-red-600 hover:bg-red-700 text-white">
              {sendingDeletion && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
